import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { DialogModule } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { EditorConfigService } from '../../../features/editor/services/editor-config.service';
import { ArgDef } from '../../models';
import { coerceToArgType, emptyValueForArgType } from '../../utils/arg-value';
import { argKeyError, findExistingKey, normalizeArgKey } from '../../utils/arg-key';

interface ChipEntry {
	key: string;
	value: unknown;
	def: ArgDef | null;
}

@Component({
	selector: 'app-attributes-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputText, ButtonModule, Popover, DialogModule, InputTextModule, Select, InputNumber],
	templateUrl: './attributes-editor.html',
	styleUrl: './attributes-editor.scss',
})
export class AttributesEditorComponent {
	private configService = inject(EditorConfigService);

	data = input<Record<string, unknown> | null>(null);
	dataChange = output<Record<string, unknown>>();

	// ── Chip model ──────────────────────────────────────────────────────
	protected chips = computed<ChipEntry[]>(() => {
		const attrs = this.data();
		if (!attrs || typeof attrs !== 'object') return [];
		const defs = this.configService.argDefs();
		return Object.entries(attrs).map(([key, value]) => ({
			key,
			value,
			def: defs.find((d) => d.key === key) ?? null,
		}));
	});

	protected availableArgs = computed(() => {
		const current = this.data() ?? {};
		return this.configService.argDefs().filter((d) => !(d.key in current));
	});

	// ── Inline edit state ───────────────────────────────────────────────
	protected editingKey = signal<string | null>(null);
	protected editingValue = signal<string>('');

	// ── New arg dialog ──────────────────────────────────────────────────
	protected newArgDialogVisible = signal(false);
	protected newArgKey = signal('');
	protected newArgType = signal<string>('string');
	protected newArgOptions = signal('');
	protected newArgDefaultValue = signal<string | number | boolean>('');
	protected readonly booleanOptions = [
		{ label: 'true', value: true },
		{ label: 'false', value: false },
	];
	protected newArgDescription = signal('');

	protected newArgKeyExists(): boolean {
		return !!findExistingKey(this.newArgKey(), this.configService.argDefs().map((d) => d.key));
	}

	/** DEV-651: why this key cannot be used, or null. Drives the inline message. */
	protected newArgKeyError(): string | null {
		return argKeyError(this.newArgKey(), this.configService.argDefs().map((d) => d.key));
	}

	// ── Actions ─────────────────────────────────────────────────────────

	protected attachArg(def: ArgDef): void {
		const current = { ...(this.data() ?? {}) };
		// DEV-749: this used to store the raw `defaultValue` / `options[0]` / `''`,
		// all of which are strings — `options` is `[String]` and the Default Value
		// input was free text. The player compares attributes strictly against
		// `player.args`, which carries real types, so a boolean attached here and
		// never hand-edited could match neither `true` nor `false`.
		const raw = def.defaultValue ?? def.options?.[0];
		current[def.key] = raw === undefined ? emptyValueForArgType(def.type) : coerceToArgType(raw, def.type);
		this.dataChange.emit(current);
	}

	protected removeArg(key: string): void {
		const current = { ...(this.data() ?? {}) };
		delete current[key];
		this.dataChange.emit(current);
	}

	protected startEdit(chip: ChipEntry): void {
		if (chip.def?.type === 'variant') return;
		this.editingKey.set(chip.key);
		this.editingValue.set(chip.value != null ? String(chip.value) : '');
	}

	protected confirmEdit(): void {
		const key = this.editingKey();
		if (!key) return;
		const current = { ...(this.data() ?? {}) };
		const chip = this.chips().find((c) => c.key === key);
		const def = chip?.def;
		current[key] = coerceToArgType(this.editingValue(), def?.type ?? 'string');
		this.editingKey.set(null);
		this.dataChange.emit(current);
	}

	protected cancelEdit(): void {
		this.editingKey.set(null);
	}

	protected onChipValueChange(key: string, value: string): void {
		const current = { ...(this.data() ?? {}) };
		const def = this.configService.argDefs().find((d) => d.key === key);
		current[key] = coerceToArgType(value, def?.type ?? 'string');
		this.dataChange.emit(current);
	}

	/**
	 * Switching the type must reset the default. Otherwise a value typed while
	 * the type was `string` survives the switch and gets stored as a string
	 * default under a boolean def — exactly the defect this change removes.
	 */
	protected onNewArgTypeChange(type: string): void {
		this.newArgType.set(type);
		this.newArgDefaultValue.set(emptyValueForArgType(type as ArgDef['type']));
	}

	protected openNewArgDialog(): void {
		this.newArgKey.set('');
		this.newArgType.set('string');
		this.newArgOptions.set('');
		this.newArgDefaultValue.set('');
		this.newArgDescription.set('');
		this.newArgDialogVisible.set(true);
	}

	protected confirmNewArg(): void {
		const key = normalizeArgKey(this.newArgKey());
		if (this.newArgKeyError()) return;
		const options = this.newArgType() === 'string' && this.newArgOptions().trim()
			? this.newArgOptions().split(',').map((s) => s.trim()).filter(Boolean)
			: undefined;
		const def: ArgDef = {
			key,
			type: this.newArgType() as ArgDef['type'],
			options,
			defaultValue: normalizeDefaultValue(this.newArgDefaultValue()),
			description: this.newArgDescription().trim() || undefined,
		};
		this.configService.addArgDef(def);
		this.newArgDialogVisible.set(false);
		// Also attach to current object
		this.attachArg(def);
	}
}

/**
 * An empty default means "no default" — but only a string can be blank, so the
 * trim/falsy check has to stay string-only. A real `false` or `0` is a genuine
 * default and must survive (`|| undefined` would have discarded both).
 */
function normalizeDefaultValue(value: string | number | boolean): string | number | boolean | undefined {
	if (typeof value === 'string') return value.trim() || undefined;
	return value;
}
